import type { StatutCommande, ValidationEtape } from '../../../mocks/commandes';

const statutLabels: Record<StatutCommande, string> = {
  EN_ATTENTE: 'Commande créée',
  EN_COUPE: 'Coupe débutée',
  EN_COUTURE: 'Couture débutée',
  TERMINE: 'Terminé',
  LIVRE: 'Livré',
  ANNULEE: 'Annulée',
};

const statutIcons: Record<StatutCommande, string> = {
  EN_ATTENTE: 'ri-file-add-line',
  EN_COUPE: 'ri-scissors-line',
  EN_COUTURE: 'ri-sewing-thread-line',
  TERMINE: 'ri-checkbox-circle-line',
  LIVRE: 'ri-truck-line',
  ANNULEE: 'ri-close-circle-line',
};

const statutColors: Record<StatutCommande, { ring: string; bg: string; text: string; line: string }> = {
  EN_ATTENTE: { ring: 'ring-yellow-400', bg: 'bg-yellow-400', text: 'text-yellow-700', line: 'bg-yellow-300' },
  EN_COUPE: { ring: 'ring-blue-400', bg: 'bg-blue-400', text: 'text-blue-700', line: 'bg-blue-300' },
  EN_COUTURE: { ring: 'ring-purple-400', bg: 'bg-purple-400', text: 'text-purple-700', line: 'bg-purple-300' },
  TERMINE: { ring: 'ring-green-400', bg: 'bg-green-400', text: 'text-green-700', line: 'bg-green-300' },
  LIVRE: { ring: 'ring-gray-400', bg: 'bg-gray-400', text: 'text-gray-600', line: 'bg-gray-300' },
  ANNULEE: { ring: 'ring-red-400', bg: 'bg-red-400', text: 'text-red-700', line: 'bg-red-300' },
};

const allSteps: StatutCommande[] = ['EN_ATTENTE', 'EN_COUPE', 'EN_COUTURE', 'TERMINE', 'LIVRE'];

interface Props {
  currentStatut: StatutCommande;
  validations: ValidationEtape[];
}

export default function CommandeTimeline({ currentStatut, validations }: Props) {
  const currentIdx = allSteps.indexOf(currentStatut);

  const getValidation = (etape: StatutCommande): ValidationEtape | undefined =>
    validations.find(v => v.etape === etape);

  return (
    <div className="relative">
      <div className="flex items-start gap-0">
        {allSteps.map((step, idx) => {
          const done = idx <= currentIdx;
          const active = idx === currentIdx;
          const val = getValidation(step);
          const colors = statutColors[step];

          return (
            <div key={step} className="flex-1 flex flex-col items-center relative">
              {/* Connector line */}
              {idx > 0 && (
                <div
                  className={`absolute top-4 right-1/2 w-full h-0.5 -translate-y-1/2 transition-colors ${
                    idx <= currentIdx ? colors.line : 'bg-gray-200'
                  }`}
                  style={{ left: '-50%' }}
                />
              )}

              {/* Step circle */}
              <div
                className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-sm ring-2 transition-all ${
                  done
                    ? `${colors.bg} ${colors.ring} text-white`
                    : 'bg-white ring-gray-200 text-gray-300'
                } ${active ? 'ring-offset-2 ring-4' : ''}`}
              >
                {done && !active ? (
                  <i className="ri-check-line text-xs font-bold" />
                ) : (
                  <i className={`${statutIcons[step]} text-xs`} />
                )}
              </div>

              {/* Step label */}
              <div className="mt-2 text-center px-1">
                <p className={`text-xs font-semibold ${done ? colors.text : 'text-gray-300'}`}>
                  {statutLabels[step]}
                </p>
                {val && (
                  <p className="text-xs text-gray-400 mt-0.5">{val.date}</p>
                )}
                {val && (
                  <p className="text-xs text-gray-500 font-medium truncate max-w-[80px]">{val.validePar.split(' ')[0]}</p>
                )}
                {!done && (
                  <p className="text-xs text-gray-300 mt-0.5">En attente</p>
                )}
              </div>

              {/* Validation comment */}
              {val?.commentaire && active && (
                <div className="mt-2 bg-white border border-gray-100 rounded-xl px-3 py-2 max-w-[140px] text-center">
                  <p className="text-xs text-gray-500 italic">&ldquo;{val.commentaire}&rdquo;</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
